import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { usePreloading } from '../../contexts/SiteContext';
import { logger } from '../../utils/logger';

// Simple utility to wrap components with motion
const createMotionComponent = (Component) => {
  return motion.create(Component);
};

const AnimatedWord = styled(motion.span)`
  display: inline-block;
  margin-right: 0.25em;
`;

const StaggerText = ({ 
  children, 
  variants, 
  containerVariants, 
  className,
  as: Component = motion.div,
  ...props 
}) => {
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const { isPreloading } = usePreloading();
  const words = children.split(' ');

  // Convert the passed component to a motion component
  const MotionComponent = createMotionComponent(Component);

  // Start animation when preloader finishes
  useEffect(() => {
    logger.debug('StaggerText effect:', { isPreloading, shouldAnimate, children });
    if (!isPreloading) {
      logger.debug('Starting stagger animation for:', children);
      setShouldAnimate(true);
    }
  }, [isPreloading, shouldAnimate, children]);

  // Default variants for elegant word animations
  const defaultVariants = {
    hidden: { 
      opacity: 0, 
      y: 20, 
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: {
        type: "spring",
        stiffness: 60,
        damping: 20,
        duration: 0.8
      }
    }
  };

  const defaultContainerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.04,
        delayChildren: 0.1
      }
    }
  };

  // Combine defaults with passed props (passed props take priority)
  const finalVariants = { ...defaultVariants, ...variants };
  const finalContainerVariants = { ...defaultContainerVariants, ...containerVariants };
  
  return (
    <MotionComponent
      className={className}
      variants={finalContainerVariants}
      initial="hidden"
      animate={shouldAnimate ? (props.whileInView ? undefined : "visible") : "hidden"}
      {...props}
    >
      {words.map((word, index) => (
        <AnimatedWord
          key={index}
          variants={finalVariants}
        >
          {word}
        </AnimatedWord>
      ))}
    </MotionComponent>
  );
};

export default StaggerText;
